import {CommonModule} from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeLinkModule} from 'se-fe-link';
import {SeFeMenuComponent, SeFeMenuModule, SeFeMenuOptions} from 'se-fe-menu';
import {SeFePaginationOptions} from 'se-fe-pagination';
import {ProductTableComponent} from '../../common/components/product-table/product-table.component';
import {ProductQueryParams} from '../../models';
import {Product} from '../../models/clare';
import {ListPageScrollService} from '../../utils/pagination-utils';
import {
  buildProductListQueryParams,
  buildProductListUrlQueryParams,
  mergeProductQueryParams,
  missingDefaultProductQueryParams,
} from './store/product-list-query-params';
import {ProductListActions} from './store/product-list.actions';
import {
  selectProductActive,
  selectProductCatalogId,
  selectProductListLoading,
  selectProductPaginationOptions,
  selectProductQuery,
  selectProductSortBy,
  selectProductSortDirection,
  selectProducts,
} from './store/product-list.selectors';
import {selectSelectedOrganizationId} from '../../state/router/router.selectors';
import {SeFeButtonModule} from 'se-fe-button';

/**
 * Displays individual products (not bundles)
 * with filtering, sorting, and pagination.
 * Used in the products" tab, and in
 * BundleChooseProductsModal
 *
 * @see BundleListComponent for bundles
 */
@Component({
  selector: 'product-list',
  templateUrl: 'product-list.component.html',
  imports: [
    CommonModule,
    ProductTableComponent,
    RouterModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeGridModule,
    SeFeIconModule,
    SeFeLinkModule,
    SeFeMenuModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../commerce-list.scss',
})
export class ProductListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private listPageScroll = inject(ListPageScrollService);

  @ViewChild('mobileMenu') public menu: SeFeMenuComponent;

  loading$: Observable<boolean> = this.store.select(selectProductListLoading);
  paginationOptions$: Observable<SeFePaginationOptions> = this.store.select(selectProductPaginationOptions);
  products$: Observable<Product[]> = this.store.select(selectProducts);
  queryString$: Observable<string> = this.store.select(selectProductQuery);
  active$: Observable<boolean | undefined> = this.store.select(selectProductActive);
  catalogId$: Observable<number | undefined> = this.store.select(selectProductCatalogId);
  sortBy$: Observable<string | undefined> = this.store.select(selectProductSortBy);
  sortDirection$: Observable<ProductQueryParams['direction']> = this.store.select(selectProductSortDirection);
  queryParams: ProductQueryParams = buildProductListQueryParams({});
  mobileMenuOptions: SeFeMenuOptions = {name: 'mobileMenu'};
  target = input<'_blank' | '_self'>('_self');
  /** Current org id from the router store, used to build the create-product link. */
  organizationId = toSignal(this.store.select(selectSelectedOrganizationId));
  /** Route for the create-product page so the anchor can open in a new tab. */
  createProductLink = computed(() => {
    const organizationId = this.organizationId();
    return organizationId ? ['/org', organizationId, 'commerce', 'products', 'new'] : null;
  });

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const queryParams = buildProductListQueryParams(params);

      if (missingDefaultProductQueryParams(params)) {
        this.navigateToQueryParams(queryParams, true);
        return;
      }

      this.queryParams = queryParams;
      this.store.dispatch(ProductListActions.loadProducts({queryParams}));
    });
  }

  queryChanged(queryParams: ProductQueryParams): void {
    const mergedParams = mergeProductQueryParams(queryParams, this.queryParams);
    this.navigateToQueryParams(mergedParams);
    this.listPageScroll.scrollToTop();
  }

  toggleActiveStatus({productId, active}: {productId: string; active: boolean}): void {
    this.store.dispatch(ProductListActions.toggleProductActiveStatus({productId, active}));
  }

  private navigateToQueryParams(queryParams: ProductQueryParams, replaceUrl = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildProductListUrlQueryParams(queryParams),
      replaceUrl,
    });
  }
}
